/**
 * Block Layout Component
 * Common layout for block components with vertical scroll area
 * Handles title, vertical scroll, and empty states
 * Inspired by _other_project BlockLayout pattern, adapted for vertical scroll
 */

"use client"

import { Badge } from "@/components/ui/badge"
import { buttonVariants } from "@/components/ui/Button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import Link from "next/link"
import React from "react"

interface BlockLayoutProps<T = any> {
  /** Title to display */
  title?: string
  /** Array of data to render (alternative to children) */
  data?: T[]
  /** Function to render each item (required if data is provided) */
  renderItem?: (item: T) => React.ReactNode
  /** Children to render inside the scroll area (alternative to data/renderItem) */
  children?: React.ReactNode
  /** URL to redirect to when the "See All" button is clicked */
  seeAllUrl?: string
  /** Custom CSS classes */
  className?: string
  /** Whether to show empty state (if false, returns null when empty) */
  showEmptyState?: boolean
  /** Empty state message */
  emptyMessage?: string
  /** Array of theme/interest names to display as badges */
  themes?: string[]
  /** Grid layout classes (default: grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3) */
  gridClasses?: string
  /** Scroll orientation: vertical (grid) or horizontal (flex) */
  orientation?: "vertical" | "horizontal"
  /** Width of each item in pixels for horizontal layout (default: 280px) */
  itemWidth?: number
}

/**
 * BlockLayout - Generic layout component with vertical ScrollArea
 *
 * Provides a consistent layout pattern for blocks with scrollable content.
 * Uses Shadcn ScrollArea component for smooth vertical scrolling with mouse wheel support.
 * Inspired by _other_project BlockLayout implementation pattern.
 *
 * Supports two usage patterns:
 * 1. With data array and renderItem function (like _other_project)
 * 2. With children (simpler, for backward compatibility)
 *
 * @example
 * ```tsx
 * // Pattern 1: With data and renderItem
 * <BlockLayout
 *   title="Events"
 *   data={events}
 *   renderItem={event => <EventCard event={event} />}
 * />
 *
 * // Pattern 2: With children
 * <BlockLayout title="Events">
 *   {events.map(event => <EventCard key={event.id} event={event} />)}
 * </BlockLayout>
 * ```
 */
export function BlockLayout<T = any>({
  title,
  data,
  renderItem,
  children,
  seeAllUrl,
  className,
  showEmptyState = false,
  emptyMessage = "No items available.",
  themes,
  gridClasses = "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3",
  orientation = "vertical",
  itemWidth = 280,
}: BlockLayoutProps<T>) {
  // Determine content to render
  let content: React.ReactNode = null
  let isEmpty = false

  if (data && renderItem) {
    // Pattern 1: Use data array and renderItem function
    isEmpty = data.length === 0
  } else if (children) {
    // Pattern 2: Use children directly
    isEmpty = React.Children.count(children) === 0
    content = children
  } else {
    // No content provided
    isEmpty = true
  }

  if (isEmpty && !showEmptyState) {
    return null
  }

  if (isEmpty && showEmptyState) {
    return (
      <section className={cn("space-y-4", className)}>
        {title && (
          <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
        )}
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </section>
    )
  }

  return (
    <div className={cn("space-y-4 p-4 bg-slate-500/5 rounded", className)}>
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          {title && (
            <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
          )}
          {seeAllUrl && (
            <Link
              href={seeAllUrl}
              className={buttonVariants({ variant: "outline" })}
            >
              See All
            </Link>
          )}
        </div>
        {/* Display themes/interests as badges */}
        {themes && themes.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {themes.map((theme, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {theme}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {data && renderItem ? (
        // Pattern 1: With data and renderItem - use ScrollArea
        <ScrollArea className="w-full" orientation={orientation}>
          {orientation === "horizontal" ? (
            <div className="flex gap-6 pb-4" style={{ width: "max-content" }}>
              {data.map((item, index) => {
                // Extract key from item if it has an id property, otherwise use index
                let key: React.Key = index
                if (item && typeof item === "object" && "id" in item) {
                  const itemId = (item as { id: React.Key }).id
                  if (itemId != null) {
                    key = itemId
                  }
                }
                return (
                  <div
                    key={key}
                    className="shrink-0"
                    style={{ width: `${itemWidth}px` }}
                  >
                    {renderItem(item)}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={cn("pr-4 pb-4", gridClasses)}>
              {data.map((item, index) => {
                // Extract key from item if it has an id property, otherwise use index
                let key: React.Key = index
                if (item && typeof item === "object" && "id" in item) {
                  const itemId = (item as { id: React.Key }).id
                  if (itemId != null) {
                    key = itemId
                  }
                }
                return (
                  <React.Fragment key={key}>{renderItem(item)}</React.Fragment>
                )
              })}
            </div>
          )}
        </ScrollArea>
      ) : (
        // Pattern 2: With children - render directly
        <ScrollArea className="w-full" orientation={orientation}>
          {orientation === "horizontal" ? (
            // For horizontal scroll, wrap children in flex layout
            <div className="flex gap-6 pb-4" style={{ width: "max-content" }}>
              {React.Children.toArray(content).map((child, index) => {
                const key =
                  React.isValidElement(child) && child.key != null
                    ? child.key
                    : index
                return (
                  <div
                    key={key}
                    className="shrink-0"
                    style={{ width: `${itemWidth}px` }}
                  >
                    {child}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="pr-4 pb-4">{content}</div>
          )}
        </ScrollArea>
      )}
    </div>
  )
}
